import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { convertFromPesewasToCedis, getUserStars } from '../../utils';
import { getUsers } from '../../redux_store/users/users_thunk'
import { getAllRides } from '../../redux_store/rides/ride_thunk'
import OverviewCard from '../templates/OverviewCard';
import { Link } from 'react-router-dom';
import { drivers, people, cash } from '../../assets/index'
import BarChart from '../templates/charts/BarChart';
import moment from 'moment';
import { ArrowRight, CarOutline, EyeEmpty } from 'iconoir-react';

const AnalyticsPage = () => {
  const ride = useSelector((state) => state.ride);
  const users = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [graphLabels, setGraphLabels] = useState({ income: [], trip: [] });
  const weekdays = ['Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat', 'Sun'];
  const [incomeGraph, setIncomeGraph] = useState([])
  const [tripsGraph, setTripsGraph] = useState([]);
  const [trips, setTrips] = useState([]);
  const [driversStats, setDriversStats] = useState(0);
  const [customersStats, setCustomersStats] = useState(0);
  const [incomeStats, setIncomeStats] = useState(0);

  useEffect(() => {
    dispatch(getUsers('all'));
    dispatch(getAllRides('all'))
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (users.users && Array.isArray(users.users)) {
      const drvers = users.users.filter((user) => user.role === 'driver');
      const customers = users.users.filter((user) => user.role === 'customer');
      setDriversStats(drvers.length);
      setCustomersStats(customers.length);
    }
    // eslint-disable-next-line
  }, [users.users]);

  useEffect(() => {
    let income = 0;
    const tripsGraphData = [], incomeGraphData = [], incomeLabel = [], tripLabel = [];
    if (ride.rides && Array.isArray(ride.rides)) {
      const days = getLast5Days();
      days.forEach((day) => {
        const dayTrips = ride.rides.filter((ride) =>
          moment(ride.createdAt).format('L') === moment(day.date).format('L')
        );

        if (dayTrips) {
          let dayTripsIncome = 0;
          dayTrips.forEach((trip) => {
            dayTripsIncome += convertFromPesewasToCedis(trip.final_total_cost)
          });
          incomeLabel.push(`${day.label} income`)
          tripLabel.push(`${day.label} trips`)
          incomeGraphData.push({
            [`${day.label} income`]: dayTripsIncome,
            day: day.label,
            tooltip: day.date
          });
          tripsGraphData.push({
            [`${day.label} trips`]: dayTrips.length,
            tooltip: day.date,
            day: day.label,
          });
        }
      });
      ride.rides.forEach((ride) => {
        income += convertFromPesewasToCedis(ride.final_total_cost);
      });
      setIncomeStats(income);
      setGraphLabels({ trip: tripLabel, income: incomeLabel });
      setTripsGraph(tripsGraphData.reverse());
      setIncomeGraph(incomeGraphData.reverse());
      let runningTrips = ride.rides.filter((ride) => ['TO PICKUP', 'AT PICKUP', 'TO DESTINATION', 'AT DESTINATION'].includes(ride.status));
      setTrips(runningTrips);
    }
    // eslint-disable-next-line    
  }, [ride.rides]);

  const getLast5Days = () => {
    const days = [];
    for (let i = 0; i < 5; i++) {
      const day = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
      days.push({ label: weekdays[day.getDay() - 1] || 'Sun', date: day });
    }
    return days;
  }

  const getStatusBadge = (status) => {
    const styles = {
      'PENDING': 'badge-warning animate-pulse',
      'TO PICKUP': 'badge bg-surface-500 text-white',
      'AT PICKUP': 'badge bg-surface-700 text-white',
      'TO DESTINATION': 'badge-primary',
      'AT DESTINATION': 'badge bg-accent-600 text-white',
      'COMPLETED': 'badge-success',
      'RATED': 'badge bg-orange-500 text-white',
    };

    const labels = {
      'PENDING': 'Pending',
      'TO PICKUP': 'To Pickup',
      'AT PICKUP': 'At Pickup',
      'TO DESTINATION': 'To Destination',
      'AT DESTINATION': 'At Destination',
      'COMPLETED': 'Completed',
      'RATED': 'Rated',
    };

    return (
      <span className={styles[status] || 'badge bg-surface-400 text-white'}>
        {labels[status] || 'Unknown'}
      </span>
    );
  };

  return (
    <div className='space-y-6'>
      {/* Page Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-surface-800'>Analytics Dashboard</h1>
          <p className='text-surface-500 mt-1'>Monitor your fleet performance and key metrics</p>
        </div>
        <div className='flex items-center gap-3'>
          <span className='text-sm text-surface-400'>Last updated: {moment().format('MMM D, YYYY h:mm A')}</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
        <OverviewCard
          label="Total Drivers"
          value={driversStats}
          icon={drivers}
          color="primary"
        />
        <OverviewCard
          label="Total Customers"
          value={customersStats}
          icon={people}
          color="accent"
        />
        <OverviewCard
          label="Total Revenue"
          value={`₵ ${incomeStats.toFixed(2)}`}
          icon={cash}
          color="success"
        />
      </div>

      {/* Charts Row */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        <div className='card'>
          <div className='flex items-center justify-between mb-6'>
            <div>
              <h3 className="text-lg font-semibold text-surface-800">Revenue Overview</h3>
              <p className="text-sm text-surface-500">Last 5 days income in GH₵</p>
            </div>
          </div>
          <div className='h-64'>
            <BarChart
              data={incomeGraph}
              keys={graphLabels.income}
              legend="income"
            />
          </div>
        </div>

        <div className='card'>
          <div className='flex items-center justify-between mb-6'>
            <div>
              <h3 className="text-lg font-semibold text-surface-800">Trip Activity</h3>
              <p className="text-sm text-surface-500">Last 5 days trip count</p>
            </div>
          </div>
          <div className='h-64'>
            <BarChart
              data={tripsGraph}
              keys={graphLabels.trip}
              legend="trips"
            />
          </div>
        </div>
      </div>

      {/* Running Trips Table */}
      <div className='card'>
        <div className='flex items-center justify-between mb-6'>
          <div className='flex items-center gap-3'>
            <div className='w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center'>
              <CarOutline strokeWidth={2} className='w-5 h-5 text-primary-600' />
            </div>
            <div>
              <h3 className='text-lg font-semibold text-surface-800'>Active Trips</h3>
              <p className='text-sm text-surface-500'>{trips.length} trips currently running</p>
            </div>
          </div>
          <Link
            to='/trips'
            className='btn-ghost text-sm text-primary-600 hover:text-primary-700 flex items-center gap-2'
          >
            View All
            <ArrowRight strokeWidth={2} className='w-4 h-4' />
          </Link>
        </div>

        <div className='overflow-x-auto'>
          <table className="table-modern">
            <thead>
              <tr>
                <th>Driver</th>
                <th>Customer</th>
                <th>Pickup</th>
                <th>Destination</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {trips && trips.length > 0 ?
                trips.map((trip, index) => (
                  <tr key={trip.id || index}>
                    {trip.driver_id ?
                      <td>
                        <div className="flex items-center gap-3">
                          <img
                            className="w-10 h-10 rounded-xl object-cover ring-2 ring-surface-100"
                            src={trip.driver.profile_pic}
                            alt=""
                            loading="lazy"
                          />
                          <div>
                            <p className="font-semibold text-surface-800">
                              {trip.driver.surname} {trip.driver.other_names}
                            </p>
                            <p className="text-xs text-surface-400 flex items-center gap-1">
                              {getUserStars(trip.driver.ratings)}
                            </p>
                          </div>
                        </div>
                      </td> :
                      <td>
                        <span className='text-surface-400 italic'>Not assigned</span>
                      </td>
                    }
                    <td>
                      <div className="flex items-center gap-3">
                        <img
                          className="w-10 h-10 rounded-xl object-cover ring-2 ring-surface-100"
                          src={trip.customer.profile_pic}
                          alt=""
                          loading="lazy"
                        />
                        <div>
                          <p className="font-semibold text-surface-800">
                            {trip.customer.surname} {trip.customer.other_names}
                          </p>
                          <p className="text-xs text-surface-400 flex items-center gap-1">
                            {getUserStars(trip.customer.ratings)}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p className='max-w-[200px] truncate' title={trip.origin_description}>
                        {trip.origin_description}
                      </p>
                    </td>
                    <td>
                      <p className='max-w-[200px] truncate' title={trip.destination_description}>
                        {trip.destination_description}
                      </p>
                    </td>
                    <td>
                      {getStatusBadge(trip.status)}
                    </td>
                    <td>
                      <Link
                        to={`/trips/${trip.id}`}
                        className='btn-ghost p-2 rounded-lg inline-flex'
                      >
                        <EyeEmpty strokeWidth={2} className='w-4 h-4' />
                      </Link>
                    </td>
                  </tr>
                )) :
                <tr>
                  <td colSpan={6} className="text-center py-12">
                    <div className='flex flex-col items-center gap-3'>
                      <div className='w-16 h-16 rounded-2xl bg-surface-100 flex items-center justify-center'>
                        <CarOutline strokeWidth={1.5} className='w-8 h-8 text-surface-400' />
                      </div>
                      <div>
                        <p className='font-medium text-surface-600'>No active trips</p>
                        <p className='text-sm text-surface-400'>All trips have been completed</p>
                      </div>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default AnalyticsPage
